import { Device } from '@twilio/voice-sdk';
import KontaVoiceApi from './kontaVoiceApiClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');
const createCallErrorEvent = detail =>
  new CustomEvent('call:error', { detail });

class KontaTelephonyClient extends EventTarget {
  constructor() {
    super();
    this.device = null;
    this.activeConnection = null;
    this.initialized = false;
    this.inboxId = null;
  }

  async initializeDevice(inboxId) {
    this.destroyDevice();

    const response = await KontaVoiceApi.getToken(inboxId);
    const { token } = response || {};
    if (!token) throw new Error('Invalid token');

    this.device = new Device(token, {
      allowIncomingWhileBusy: true,
      disableAudioContextSounds: true,
    });

    this.device.removeAllListeners();
    this.device.on('connect', conn => {
      this.activeConnection = conn;
      conn.on('disconnect', this.onDisconnect);
    });

    this.device.on('disconnect', this.onDisconnect);

    this.device.on('tokenWillExpire', async () => {
      const r = await KontaVoiceApi.getToken(this.inboxId);
      if (r?.token) this.device.updateToken(r.token);
    });

    this.device.on('error', error => {
      // eslint-disable-next-line no-console
      console.error(
        '[KontaVoice] Device error:',
        error?.code,
        error?.message,
        error?.twilioError || error
      );
      this.dispatchEvent(
        createCallErrorEvent({ code: error?.code, message: error?.message })
      );
    });

    this.initialized = true;
    this.inboxId = inboxId;

    return this.device;
  }

  get hasActiveConnection() {
    return !!this.activeConnection;
  }

  endClientCall() {
    if (this.activeConnection) {
      this.activeConnection.disconnect();
    }
    this.activeConnection = null;
    if (this.device) {
      this.device.disconnectAll();
    }
  }

  destroyDevice() {
    if (this.device) {
      this.device.destroy();
    }
    this.activeConnection = null;
    this.device = null;
    this.initialized = false;
    this.inboxId = null;
  }

  async joinClientCall({ to, conversationId }) {
    if (!this.device || !this.initialized || !to) return null;
    if (this.activeConnection) return this.activeConnection;

    const params = {
      To: to,
      is_agent: 'true',
      conversation_id: conversationId,
    };

    // eslint-disable-next-line no-console
    console.info('[KontaVoice] connecting agent leg with params:', params);

    const connection = await this.device.connect({ params });
    this.activeConnection = connection;

    ['accept', 'ringing', 'reject', 'cancel'].forEach(evt => {
      connection.on(evt, () => {
        // eslint-disable-next-line no-console
        console.info(`[KontaVoice] agent call event: ${evt}`);
      });
    });
    connection.on('error', error => {
      // eslint-disable-next-line no-console
      console.error(
        '[KontaVoice] agent call error:',
        error?.code,
        error?.message,
        error?.twilioError || error
      );
      this.dispatchEvent(
        createCallErrorEvent({ code: error?.code, message: error?.message })
      );
    });
    connection.on('disconnect', this.onDisconnect);

    return connection;
  }

  onDisconnect = () => {
    this.activeConnection = null;
    this.dispatchEvent(createCallDisconnectedEvent());
  };
}

export default new KontaTelephonyClient();
